import Icon from '@/icons';
import { useVoice } from '@/VoiceSystem';
import { useState, useEffect, useRef } from 'react';

function Dropdown({ options, selected, onChange, label }: { options: MediaDeviceInfo[], selected: MediaDeviceInfo | null, onChange: (option: MediaDeviceInfo) => void, label: string }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative w-full">
            <button
                className="w-full bg-brand-dark text-gray-200 rounded-lg p-2 text-left"
                onClick={() => setIsOpen(!isOpen)}
            >
                {selected ? selected.label : `Select ${label}`}
            </button>
            {isOpen && (
                <div className="absolute z-10 w-full bg-brand-dark text-gray-200 rounded-lg mt-1">
                    {options.map((option) => (
                        <div
                            key={option.deviceId}
                            className={`p-2 hover:bg-brand-default cursor-pointer ${selected?.label === option.label ? 'bg-brand-darkest' : ''}`}
                            onClick={() => {
                                onChange(option);
                                setIsOpen(false);
                            }}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function RangeInput({ value, onChange, min, max }: any) {
    const [, setIsDragging] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const handleMouseDown = (e: any) => {
        if (e.target !== containerRef.current) {
            updateValueFromPosition(e.clientX);
        }
        setIsDragging(true);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e: any) => {
        updateValueFromPosition(e.clientX);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const updateValueFromPosition = (clientX: number) => {
        if (containerRef.current) {
            const { left, width } = containerRef.current.getBoundingClientRect();
            const position = Math.min(width, Math.max(0, clientX - left));
            const newValue = (position / width) * (max - min) + min;
            setCurrentValue(newValue);
            onChange(newValue);
        }
    };

    const thumbPosition = ((currentValue - min) / (max - min)) * 100;

    return (
        <div className="w-full mt-4 rounded relative">
            <div
                ref={containerRef}
                className="w-full bg-brand-default h-2 mt-2 group rounded relative"
                onMouseDown={handleMouseDown}
            >
                <div
                    className="h-full bg-gradient-to-r from-blue-700 to-blue-400 rounded"
                    style={{ width: `${thumbPosition + 1}%` }}
                ></div>

                <div className='hidden group-hover:flex absolute -bottom-10 h-8 w-12 rounded items-center justify-center bg-brand-dark bg-opacity-50 '
                    style={{ left: `${thumbPosition}%` }}
                >
                    <span className="text-gray-300">%{Math.round(currentValue)}</span>
                </div>

                <div
                    className="absolute h-4 w-4 bg-white rounded-full -top-1/2 shadow-lg cursor-grab hover:scale-110 transform transition-transform duration-300"
                    style={{ left: `${thumbPosition - 1}%` }}
                ></div>
            </div>
        </div>
    );
}


export default function VoiceSettings() {
    const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
    const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
    const [mic, setMic] = useState<MediaDeviceInfo | null>(localStorage.getItem('voiceSettings') ? JSON.parse(localStorage.getItem('voiceSettings')!).mic : null);
    const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
    const [speaker, setSpeaker] = useState<MediaDeviceInfo | null>(localStorage.getItem('voiceSettings') ? JSON.parse(localStorage.getItem('voiceSettings')!).speaker : null);
    const [isTesting, setIsTesting] = useState(false);
    const [micLevel, setMicLevel] = useState(0);
    const [MicrophoneLevel, setMicrophoneLevel] = useState(JSON.parse(localStorage.getItem('voiceSettings') || '{}').MicrophoneLevel || 100);
    const [SpeakerLevel, setSpeakerLevel] = useState(JSON.parse(localStorage.getItem('voiceSettings') || '{}').SpeakerLevel || 100);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const microphoneRef = useRef<MediaStream | null>(null);
    const speakerAudioRef = useRef<MediaStreamAudioDestinationNode | null>(null);
    const voice = useVoice();
    useEffect(() => {
        const allMicrophones = () => {
            navigator.mediaDevices.getUserMedia({ audio: true })
                .then(() => {
                    setIsAuthorized(true);
                })
                .catch(() => {
                    setIsAuthorized(false);
                });

            if (isAuthorized) {
                navigator.mediaDevices.enumerateDevices()
                    .then((devices) => {
                        const mics = devices.filter((device) => device.kind === 'audioinput');
                        setMics(mics);

                        const speakers = devices.filter((device) => device.kind === 'audiooutput');
                        setSpeakers(speakers);
                    });
            }
        };

        allMicrophones();
    }, [isAuthorized]);

    useEffect(() => {
        localStorage.setItem('voiceSettings', JSON.stringify({ mic, speaker, MicrophoneLevel, SpeakerLevel }));

        voice.setVoiceSettings({ mic, speaker, MicrophoneLevel, SpeakerLevel });
    }, [mic, speaker, MicrophoneLevel, SpeakerLevel]);

    const requestMicrophone = () => {
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(() => {
                setIsAuthorized(true);
            })
            .catch(() => {
                setIsAuthorized(false);
            });
    };

    const startMicTest = () => {
        if (mic) {
            navigator.mediaDevices.getUserMedia({ audio: { deviceId: mic.deviceId } })
                .then((stream) => {
                    audioContextRef.current = new AudioContext();
                    analyserRef.current = audioContextRef.current.createAnalyser();
                    analyserRef.current.fftSize = 128;
                    microphoneRef.current = stream;
                    const source = audioContextRef.current.createMediaStreamSource(stream);
                    source.connect(analyserRef.current);
                    analyserRef.current.connect(audioContextRef.current.destination);

                    speakerAudioRef.current = audioContextRef.current.createMediaStreamDestination();
                    const speakerSource = audioContextRef.current.createMediaStreamSource(speakerAudioRef.current.stream);
                    speakerSource.connect(audioContextRef.current.destination);

                    const bufferLength = analyserRef.current.frequencyBinCount;
                    const dataArray = new Uint8Array(bufferLength);

                    const draw = () => {
                        analyserRef.current?.getByteFrequencyData(dataArray);
                        const average = dataArray.reduce((a, b) => a + b) / bufferLength;
                        setMicLevel(average);
                        requestAnimationFrame(draw);
                    };

                    draw();
                }
            );
        }
    }

    const stopMicTest = () => {
        if (microphoneRef.current) {
            microphoneRef.current.getTracks().forEach(track => track.stop());
            microphoneRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        analyserRef.current = null;
        speakerAudioRef.current = null;
    
        setMicLevel(0);
    };
            

    useEffect(() => {
        if (isTesting) {
            startMicTest();
        } else {
            stopMicTest();
        }
    }, [isTesting]);

    return (
        <div className="flex flex-col items-start w-full focus:outline-none">
            {isAuthorized == false && (
                <div className="flex items-center w-full bg-red-400/50 p-4 rounded-lg">
                    <h1 className="font-semibold text-lg text-left">
                        <Icon name="x" size="24" />
                    </h1>
                    <p className="text-gray-300 text-left">
                        Microphone access is required to use voice settings.
                    </p>
                </div>
            )}

            <div className='grid sm:grid-cols-2 gap-8 w-full py-3'>
                <div className="flex flex-col items-start w-full">
                    <h1 className="font-semibold text-lg text-left">
                        Microphone
                    </h1>
                    <Dropdown
                        options={mics}
                        selected={mic}
                        onChange={setMic}
                        label="Microphone"
                    />

                    <RangeInput
                        value={MicrophoneLevel}
                        onChange={setMicrophoneLevel}
                        min={0}
                        max={150}
                    />

                </div>
                <div className="flex flex-col items-start w-full">
                    <h1 className="font-semibold text-lg text-left">
                        Speaker
                    </h1>
                    <Dropdown
                        options={speakers}
                        selected={speaker}
                        onChange={setSpeaker}
                        label="Speaker"
                    />

                    <RangeInput
                        value={SpeakerLevel}
                        onChange={setSpeakerLevel}
                        min={0}
                        max={200}
                    />
                </div>
            </div>

            {!isAuthorized && (
                <button
                    className="bg-brand-dark flex text-gray-200 rounded-lg p-2"
                    onClick={requestMicrophone}
                >
                    Allow Microphone Access
                </button>
            )}

            {isAuthorized && (
                <div className='flex w-full justify-between items-center'>
                    <div className='flex flex-col'>
                    <h1 className="font-semibold text-sm text-left">
                        Test Microphone
                    </h1>

                    <p className="text-gray-300 text-left">
                        Test your microphone to see the input level
                    </p>
                    </div>
                    <div
                        className={`relative inline-flex items-center cursor-pointer`}
                        onClick={() => setIsTesting(!isTesting)}
                    >
                        <input
                            type="checkbox"
                            className="sr-only"
                            checked={isTesting}
                            onChange={() => setIsTesting(!isTesting)}
                        />
                        <div
                            className={`block w-14 h-8 rounded-full ${isTesting ? 'bg-brand-green' : 'bg-brand-profile' }`}
                        >
                            <div
                                className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${isTesting ? 'translate-x-6' : 'translate-x-0'}`}
                            />
                        </div>
                    </div>
                </div>
            )}




            <div className="w-full mt-4 rounded">
                <h2 className="text-lg font-semibold text-gray-300">Input Level</h2>
                <div className="w-full bg-brand-default h-2 mt-2 rounded">
                    <div
                        className="h-full bg-gradient-to-r from-blue-700 to-blue-400 rounded"
                        style={{ width: `${(micLevel / 255) * 100}%` }}
                    ></div>
                </div>
            </div>
        </div>
    );
}
